/**
 * **The unit panel** (`projects/unit-panel.md` Phase 2): the unit column's bottom slot. Collapsed it is
 * the {@link UnitIntelCard}; the expand control on the selection's card (none on a hover preview)
 * grows it in place into the full sheet, `sheet` (`InspectorBody`, with `inspectorBodyProps` from
 * `CombatApp.tsx`), in the card's house style (Phase 4: its frame, side stripe and type scale, the
 * header where the card's was) and scrolling inside the column's height. The collapse control, Esc (layer 5) and a deselect fold it back.
 */
import type { ReactNode } from "react";
import type { DisplayCombatant } from "@lancer-vtt/domain";
import type { UnitSheetIdentity } from "./combatWindows/UnitSheet";
import { faDownLeftAndUpRightToCenter } from "@fortawesome/free-solid-svg-icons";
import { Icon } from "../Icon";
import { UnitIntelCard } from "./UnitIntelCard";

export function UnitPanel({
  combatant,
  identity,
  preview,
  sheet,
  onExpand,
  onCollapse,
}: {
  combatant: DisplayCombatant;
  identity: UnitSheetIdentity;
  preview: boolean;
  /** The expanded sheet's body, or `null` for the collapsed card. */
  sheet: ReactNode | null;
  /** Grow into the sheet; `undefined` shows no expand control (a hover preview, a leaving copy). */
  onExpand?: (() => void) | undefined;
  onCollapse: () => void;
}) {
  if (sheet === null) return <UnitIntelCard combatant={combatant} identity={identity} preview={preview} onExpand={onExpand} />;
  return (
    <section className={`uic is-sheet${identity.side === "npc" && !identity.tag ? " is-hostile" : ""}`} data-unit-id={combatant.id} aria-label={`${identity.name} sheet`}>
      {/* The sheet's own header names the unit, set in the card's type (`unit-intel-card.css`); the
          collapse control sits in its corner where the card's expand control was. */}
      <button type="button" className="uic-expand uic-sheet-collapse" aria-expanded={true} aria-label={`Collapse ${identity.name}'s sheet`} title="Collapse" onClick={onCollapse}>
        <Icon icon={faDownLeftAndUpRightToCenter} />
      </button>
      <div className="uic-sheet-body">{sheet}</div>
    </section>
  );
}
